<template>
	<Screen>
		<TopBar variant="home" search />

		<component
			:is="banner.link ? 'a' : 'div'"
			:href="banner.link || undefined"
			class="flex min-h-tap items-center justify-center gap-2 bg-tint px-4 py-2 text-center text-[13px] font-bold"
		>
			<Icon v-if="!banner.custom" name="truck" :size="18" />
			<span>{{ banner.text }}</span>
		</component>

		<div class="grid grid-cols-2 gap-2.5 px-4 pt-3.5">
			<router-link
				v-for="(tile, i) in tiles"
				:key="tile.title"
				:to="tile.to"
				class="relative block overflow-hidden rounded-2xl text-white"
				:class="i === 0 ? 'col-span-2 h-[196px] min-[420px]:h-[220px]' : 'h-[148px] min-[420px]:h-[164px]'"
			>
				<img :src="tile.image" alt="" class="absolute inset-0 h-full w-full object-cover" />
				<span class="absolute inset-0" style="background: linear-gradient(to top, rgba(20, 14, 24, 0.74), rgba(20, 14, 24, 0) 66%)" />
				<span v-if="i === 0" class="absolute inset-x-4 bottom-4 flex items-end justify-between gap-3">
					<span>
						<span class="block text-[26px] font-extrabold leading-tight">{{ tile.title }}</span>
						<span class="mt-1 block text-sm font-medium">{{ tile.description }}</span>
					</span>
					<span class="flex h-9 flex-none items-center rounded-full bg-white/95 px-3.5 text-sm font-bold text-[#29242e]">Shop now</span>
				</span>
				<span v-else class="absolute inset-x-3 bottom-3">
					<span class="block text-[17px] font-extrabold leading-tight">{{ tile.title }}</span>
					<span class="mt-0.5 block text-xs font-medium leading-snug">{{ tile.description }}</span>
				</span>
			</router-link>
		</div>

		<section v-if="popular.length || loadingPopular" class="pt-5">
			<h2 class="px-4 text-xl font-extrabold">Popular right now</h2>
			<div class="no-scrollbar flex snap-x scroll-px-4 gap-3 overflow-x-auto px-4 pb-1 pt-1.5">
				<template v-if="loadingPopular">
					<div v-for="n in 3" :key="n" class="w-[148px] flex-none">
						<div class="h-[148px] animate-pulse rounded-[14px] bg-field" />
						<div class="mt-2 h-4 w-3/4 animate-pulse rounded bg-field" />
					</div>
				</template>
				<ProductCard v-for="product in popular" v-else :key="product.id" :product="product" class="w-[148px] flex-none snap-start" compact />
			</div>
		</section>

		<section class="px-4 pt-7">
			<button
				type="button"
				class="relative block aspect-video w-full overflow-hidden rounded-[18px] bg-[#29242e] text-left"
				aria-label="Play video: Meet In His Hands"
				@click="playVideo"
			>
				<img src="/images/intro-video-poster.jpg" alt="" class="absolute inset-0 h-full w-full object-cover" />
				<span class="absolute inset-0" style="background: linear-gradient(to top, rgba(20, 14, 24, 0.78), rgba(20, 14, 24, 0.05) 70%)" />
				<span class="absolute left-1/2 top-1/2 -ml-8 -mt-11 flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-[#29242e] shadow-lg">
					<Icon name="play" :size="26" class="ml-[3px]" />
				</span>
				<span class="absolute inset-x-4 bottom-3.5 text-white">
					<span class="block text-xl font-extrabold">Meet In His Hands</span>
					<span class="block text-sm font-medium">Intro video · Learn more about us</span>
				</span>
			</button>
		</section>

		<section class="space-y-3 px-4 pt-7">
			<img src="/images/baby-parent-hands0129.jpg" alt="A newborn held in a parent’s hands" class="h-[170px] w-full rounded-2xl object-cover" />
			<h2 class="pt-1 text-2xl font-extrabold">Our Mission</h2>
			<p class="text-base font-medium leading-relaxed text-muted">
				At In His Hands we are your source for all things birth. Whether it be for your birth or those you are serving, we provide the very best products with the
				highest level of service to help make the birth experience the very best it can be.
			</p>
			<p class="text-base font-medium leading-relaxed text-muted">
				In His Hands is a Christian company. Our name points to our faith, that God holds all things In His Hands including all the babies He creates and brings into
				the world.
			</p>
			<router-link to="/about-us" class="btn-secondary !w-auto">Our full story</router-link>
		</section>

		<VideoPlayer v-if="showVideo" @close="closeVideo" />
	</Screen>
</template>

<script>
import Screen from '@/components/Screen.vue'
import TopBar from '@/components/TopBar.vue'
import Icon from '@/components/Icon.vue'
import ProductCard from '@/components/ProductCard.vue'
import VideoPlayer from '@/components/VideoPlayer.vue'
import axios from 'axios'
import Mango from '@front/helpers/mango'
import { PRODUCT_CARD_FIELDS } from '@/lib/products'
import { openSheet, closeSheet, isSheetOpen } from '@/lib/sheets'

export default {
	components: { Screen, TopBar, Icon, ProductCard, VideoPlayer },
	inject: ['store'],
	data() {
		return {
			popular: [],
			loadingPopular: true,
			// The website's home tiles (front/src/components/pages/home.vue).
			tiles: [
				{ title: 'Birth Kits', description: 'Find your midwife’s custom birth kit', image: '/images/find_your_kit_resized.jpeg', to: '/kits' },
				{
					title: 'Midwifery Equipment',
					description: 'Products for midwives, doulas and more',
					image: '/images/midwife-supplies_resized.jpeg',
					to: '/categories/midwifery-supplies',
				},
				{ title: 'Water Birth', description: 'Everything you need for a water birth', image: '/images/shop_water_birth.jpeg', to: '/categories/water-birth' },
				{ title: 'Herbal & Natural', description: 'Top quality natural products', image: '/images/natural-herbal-2-0129-2.jpg', to: '/categories/herbal-natural' },
				{ title: 'For Mothers', description: 'Pregnancy & postpartum essentials', image: '/images/birth-supplies0129.jpg', to: '/categories/mom-baby' },
			],
		}
	},
	computed: {
		// The player is a full-screen dialog in the URL, so Back closes it.
		showVideo() {
			return isSheetOpen(this.$route, 'video')
		},
		// The site-wide announcement from the admin (front/src/components/layout/banner.vue), else free shipping.
		banner() {
			const active = this.store.banner
			if (active?.text) return { text: active.text, link: active.link, custom: true }
			return { text: 'Free shipping on orders over $125', link: null, custom: false }
		},
	},
	async created() {
		this.loadBanner()
		try {
			const products = await Mango.products({ search: { popular: true }, limit: 20, fields: [...PRODUCT_CARD_FIELDS, 'popularOrder'] })
			this.popular = (products || []).sort((a, b) => (a.popularOrder ?? 99) - (b.popularOrder ?? 99))
		} catch {
			this.popular = []
		} finally {
			this.loadingPopular = false
		}
	},
	methods: {
		async loadBanner() {
			if (this.store.banner !== undefined) return
			try {
				const { data } = await axios.get(`${this.store.api}/controllers/banner/active`)
				const expired = data?.autoHideAfterDate && new Date() >= new Date(data.autoHideAfterDate)
				const text = data?.messageTextMobile || data?.messageText
				this.store.banner = data && !expired && text ? { text, link: data.isClickable ? data.clickLink : data.showButton ? data.buttonLink : null } : null
			} catch {
				this.store.banner = null
			}
		},
		playVideo() {
			openSheet('video')
		},
		closeVideo() {
			closeSheet('video')
		},
	},
}
</script>
